import { useEffect, useState } from 'react';
import {
  apiErrorText,
  useBindAccumulationContractMutation,
  useGetSmetaInfoQuery,
  useImportAccumulationFromKsPackageMutation,
  useListKsPackagesQuery,
  useListSmetaContextOptionsQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Label, Select } from '@shared/ui/Input';
import {
  pickScope,
  pickedKsPackageId,
  releasedKsPackages,
  type KsPackagePick,
} from './ksPackagePick';
import {
  ErrMargin,
  FieldSelect,
  FieldSelectWide,
  FiltersMargin,
  MutedMargin,
} from './styled';

/** Итог последнего переноса — для той ведомости и того договора, где он сделан. */
interface ImportNote {
  scope: string;
  text: string;
  failed: boolean;
}

interface AccumulationBindingPanelProps {
  ledgerId: string;
  initialContractId?: string | null;
  initialProjectId?: string | null;
  canWrite: boolean;
}

export function AccumulationBindingPanel({
  ledgerId,
  initialContractId,
  initialProjectId,
  canWrite,
}: AccumulationBindingPanelProps) {
  const [bindContractId, setBindContractId] = useState(initialContractId ?? '');
  const [bindProjectId, setBindProjectId] = useState(initialProjectId ?? '');
  // Выбор пакета и итог переноса помнят свою ведомость и договор: при смене
  // любого из них прежние выбор и сообщение больше не показываются и не
  // отправляются.
  const scope = pickScope(ledgerId, initialContractId);
  const [pick, setPick] = useState<KsPackagePick>({ scope: '', packageId: '' });
  const [importNote, setImportNote] = useState<ImportNote | null>(null);
  const [error, setError] = useState<string | null>(null);

  const smetaInfoQ = useGetSmetaInfoQuery();
  const ksPackagesAvailable = smetaInfoQ.data?.flags?.ks_package ?? false;
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  // В ленту переносится только выпущенный пакет договора, к которому привязана
  // ведомость: сервер откажет черновику и пакету чужого договора, поэтому их
  // и не предлагаем. Без привязки показываем выпущенные пакеты всех договоров —
  // сервер примет пакет договора с тем же номером, что у ведомости.
  const ksPackagesQ = useListKsPackagesQuery(
    initialContractId ? { contract_id: initialContractId } : undefined,
    { skip: !ksPackagesAvailable },
  );
  // `currentData`, а не `data`: пока грузится список другого договора, `data`
  // ещё держит пакеты прежнего.
  const releasedPackages = releasedKsPackages(ksPackagesQ.currentData?.items ?? []);
  const selectedKsPackageId = pickedKsPackageId(pick, scope, releasedPackages);
  const shownImportNote = importNote?.scope === scope ? importNote : null;
  const [bindContract, { isLoading: bindingContract }] = useBindAccumulationContractMutation();
  const [importFromKsPackage, { isLoading: importingKsPkg }] =
    useImportAccumulationFromKsPackageMutation();

  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];

  useEffect(() => {
    setBindContractId(initialContractId ?? '');
    setBindProjectId(initialProjectId ?? '');
  }, [initialContractId, initialProjectId]);

  async function handleBindContract() {
    if (!ledgerId) return;
    setError(null);
    try {
      await bindContract({
        id: ledgerId,
        statut_contract_id: bindContractId || null,
        project_id: bindProjectId || null,
      }).unwrap();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить привязку к договору/объекту'));
    }
  }

  async function handleImportFromKsPackage() {
    // Только пакет из актуального списка этой ведомости (`pickedKsPackageId`).
    const packageId = selectedKsPackageId;
    const requestScope = scope;
    if (!ledgerId || !packageId) return;
    setImportNote(null);
    try {
      const res = await importFromKsPackage({
        id: ledgerId,
        package_id: packageId,
      }).unwrap();
      const replaced = res.items.reduce((sum, it) => sum + (it.replaced_pa_entries ?? 0), 0);
      setImportNote({
        scope: requestScope,
        failed: false,
        text:
          `Перенесено в ленту строк: ${res.entries_synced} (пакетов КС-3: ${res.packages_synced}).` +
          (replaced > 0
            ? ` Заменено прежних строк тех же актов: ${replaced} — повторный перенос не задваивает объём.`
            : ''),
      });
    } catch (err) {
      setImportNote({
        scope: requestScope,
        failed: true,
        text: apiErrorText(err, 'Не удалось перенести позиции пакета КС-3 в ленту'),
      });
    }
  }

  return (
    <>
      <FiltersMargin>
        <FieldSelect>
          <Label>Договор Статута (мост в Пакет КС и Прогресс)</Label>
          <Select
            value={bindContractId}
            onChange={(e) => setBindContractId(e.target.value)}
            disabled={!canWrite}
          >
            <option value="">— не привязан —</option>
            {contextContracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                {(c.subject || c.counterparty || '').slice(0, 40)}
              </option>
            ))}
          </Select>
        </FieldSelect>
        <FieldSelect>
          <Label>Проект ПТО / объект</Label>
          <Select
            value={bindProjectId}
            onChange={(e) => setBindProjectId(e.target.value)}
            disabled={!canWrite}
          >
            <option value="">— не привязан —</option>
            {contextProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.code} · ` : ''}
                {p.name}
              </option>
            ))}
          </Select>
        </FieldSelect>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          disabled={bindingContract || !canWrite}
          onClick={handleBindContract}
        >
          {bindingContract ? 'Сохраняю…' : 'Сохранить привязку'}
        </Button>
      </FiltersMargin>
      {error && <ErrMargin>{error}</ErrMargin>}

      {ksPackagesAvailable && (
        <>
          <FiltersMargin>
            <FieldSelectWide>
              <Label>Перенос из выпущенного пакета КС-3 (без повторной загрузки Excel)</Label>
              <Select
                value={selectedKsPackageId}
                onChange={(e) => setPick({ scope, packageId: e.target.value })}
                disabled={!canWrite}
              >
                <option value="">— выберите выпущенный пакет КС-3 —</option>
                {releasedPackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    КС-3 №{pkg.ks3_no} ({pkg.period_from} — {pkg.period_to})
                    {pkg.status === 'archived' ? ' · в архиве' : ''}
                  </option>
                ))}
              </Select>
            </FieldSelectWide>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={importingKsPkg || !selectedKsPackageId || !canWrite}
              onClick={handleImportFromKsPackage}
            >
              {importingKsPkg ? 'Переношу…' : 'Перенести позиции пакета в ленту'}
            </Button>
          </FiltersMargin>
          {shownImportNote &&
            (shownImportNote.failed ? (
              <ErrMargin>{shownImportNote.text}</ErrMargin>
            ) : (
              <MutedMargin>{shownImportNote.text}</MutedMargin>
            ))}
        </>
      )}
    </>
  );
}
